import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DateRangeType } from "../../ui/DateRangeSelector";
import { ExportUsersCsvParams } from "../../../apis/admin";

export type UserListType = "VENDOR" | "BUYER" | "ADMIN";

export type UserSortBy = NonNullable<ExportUsersCsvParams["sortBy"]>;
export type UserSortOrder = NonNullable<ExportUsersCsvParams["sortOrder"]>;

const parseOptionalNumber = (raw: string): number | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
};

export interface UseUserFiltersReturn {
  inputValue: string;
  setInputValue: (v: string) => void;
  search: string;
  createdRange: DateRangeType;
  setCreatedRange: (r: DateRangeType) => void;
  minOrderAmountInput: string;
  setMinOrderAmountInput: (v: string) => void;
  maxOrderAmountInput: string;
  setMaxOrderAmountInput: (v: string) => void;
  minOrderCountInput: string;
  setMinOrderCountInput: (v: string) => void;
  maxOrderCountInput: string;
  setMaxOrderCountInput: (v: string) => void;
  minProductCountInput: string;
  setMinProductCountInput: (v: string) => void;
  maxProductCountInput: string;
  setMaxProductCountInput: (v: string) => void;
  sortBy: UserSortBy | null;
  setSortBy: (v: UserSortBy | null) => void;
  sortOrder: UserSortOrder | null;
  setSortOrder: (v: UserSortOrder | null) => void;
  page: number;
  setPage: (v: number) => void;
  limit: number;
  setLimit: (v: number) => void;
  filterResetKey: number;
  bumpFilterResetKey: () => void;
  handleClearFilters: () => void;
  activeFilterCount: number;
  hasActiveFilters: boolean;
  listParams: Record<string, unknown>;
  exportParams: Omit<ExportUsersCsvParams, "page" | "limit">;
}

export const useUserFilters = (
  userType: UserListType,
): UseUserFiltersReturn => {
  const [inputValue, setInputValue] = useState("");
  const [search, setSearch] = useState("");
  const [createdRange, setCreatedRangeState] = useState<DateRangeType>({
    startDate: null,
    endDate: null,
  });
  const [minOrderAmountInput, setMinOrderAmountInput] = useState("");
  const [maxOrderAmountInput, setMaxOrderAmountInput] = useState("");
  const [minOrderCountInput, setMinOrderCountInput] = useState("");
  const [maxOrderCountInput, setMaxOrderCountInput] = useState("");
  const [minProductCountInput, setMinProductCountInput] = useState("");
  const [maxProductCountInput, setMaxProductCountInput] = useState("");
  const [minOrderAmount, setMinOrderAmount] = useState<number | null>(null);
  const [maxOrderAmount, setMaxOrderAmount] = useState<number | null>(null);
  const [minOrderCount, setMinOrderCount] = useState<number | null>(null);
  const [maxOrderCount, setMaxOrderCount] = useState<number | null>(null);
  const [minProductCount, setMinProductCount] = useState<number | null>(null);
  const [maxProductCount, setMaxProductCount] = useState<number | null>(null);
  const [sortBy, setSortByState] = useState<UserSortBy | null>(null);
  const [sortOrder, setSortOrderState] = useState<UserSortOrder | null>(null);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const appliedSearchRef = useRef(search);
  const appliedRangesRef = useRef({
    minOrderAmount,
    maxOrderAmount,
    minOrderCount,
    maxOrderCount,
    minProductCount,
    maxProductCount,
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      if (appliedSearchRef.current !== inputValue) {
        appliedSearchRef.current = inputValue;
        setSearch(inputValue);
        setPage(0);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const next = {
        minOrderAmount: parseOptionalNumber(minOrderAmountInput),
        maxOrderAmount: parseOptionalNumber(maxOrderAmountInput),
        minOrderCount: parseOptionalNumber(minOrderCountInput),
        maxOrderCount: parseOptionalNumber(maxOrderCountInput),
        minProductCount: parseOptionalNumber(minProductCountInput),
        maxProductCount: parseOptionalNumber(maxProductCountInput),
      };
      const prev = appliedRangesRef.current;
      const changed = (
        Object.keys(next) as Array<keyof typeof next>
      ).some((key) => next[key] !== prev[key]);
      if (!changed) return;

      appliedRangesRef.current = next;
      setMinOrderAmount(next.minOrderAmount);
      setMaxOrderAmount(next.maxOrderAmount);
      setMinOrderCount(next.minOrderCount);
      setMaxOrderCount(next.maxOrderCount);
      setMinProductCount(next.minProductCount);
      setMaxProductCount(next.maxProductCount);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [
    minOrderAmountInput,
    maxOrderAmountInput,
    minOrderCountInput,
    maxOrderCountInput,
    minProductCountInput,
    maxProductCountInput,
  ]);

  const setCreatedRange = useCallback((r: DateRangeType) => {
    setCreatedRangeState(r);
    setPage(0);
  }, []);

  const setSortBy = useCallback((v: UserSortBy | null) => {
    setSortByState(v);
    setPage(0);
  }, []);

  const setSortOrder = useCallback((v: UserSortOrder | null) => {
    setSortOrderState(v);
    setPage(0);
  }, []);

  const bumpFilterResetKey = useCallback(() => {
    setFilterResetKey((k) => k + 1);
  }, []);

  const handleClearFilters = useCallback(() => {
    setInputValue("");
    setSearch("");
    setCreatedRangeState({ startDate: null, endDate: null });
    setMinOrderAmountInput("");
    setMaxOrderAmountInput("");
    setMinOrderCountInput("");
    setMaxOrderCountInput("");
    setMinProductCountInput("");
    setMaxProductCountInput("");
    setMinOrderAmount(null);
    setMaxOrderAmount(null);
    setMinOrderCount(null);
    setMaxOrderCount(null);
    setMinProductCount(null);
    setMaxProductCount(null);
    setSortByState(null);
    setSortOrderState(null);
    appliedSearchRef.current = "";
    appliedRangesRef.current = {
      minOrderAmount: null,
      maxOrderAmount: null,
      minOrderCount: null,
      maxOrderCount: null,
      minProductCount: null,
      maxProductCount: null,
    };
    setFilterResetKey((k) => k + 1);
    setPage(0);
  }, []);

  const isCreatedRangeActive = !!(
    createdRange.startDate || createdRange.endDate
  );
  const activeFilterCount =
    (search ? 1 : 0) +
    (isCreatedRangeActive ? 1 : 0) +
    (minOrderAmount != null || maxOrderAmount != null ? 1 : 0) +
    (minOrderCount != null || maxOrderCount != null ? 1 : 0) +
    (minProductCount != null || maxProductCount != null ? 1 : 0) +
    (sortBy ? 1 : 0) +
    (sortOrder ? 1 : 0);
  const hasActiveFilters = activeFilterCount > 0;

  const listParams = useMemo(
    () => ({
      page: page + 1,
      limit,
      userType,
      filter: search || undefined,
      startDate: createdRange.startDate || undefined,
      endDate: createdRange.endDate || undefined,
      minOrderAmount: minOrderAmount ?? undefined,
      maxOrderAmount: maxOrderAmount ?? undefined,
      minOrderCount: minOrderCount ?? undefined,
      maxOrderCount: maxOrderCount ?? undefined,
      minProductCount: minProductCount ?? undefined,
      maxProductCount: maxProductCount ?? undefined,
      sortBy: sortBy || undefined,
      sortOrder: sortBy ? sortOrder || undefined : undefined,
    }),
    [
      page,
      limit,
      userType,
      search,
      createdRange,
      minOrderAmount,
      maxOrderAmount,
      minOrderCount,
      maxOrderCount,
      minProductCount,
      maxProductCount,
      sortBy,
      sortOrder,
    ],
  );

  const exportParams = useMemo<
    Omit<ExportUsersCsvParams, "page" | "limit">
  >(
    () => ({
      userType,
      filter: search || undefined,
      startDate: createdRange.startDate || undefined,
      endDate: createdRange.endDate || undefined,
      minOrderAmount: minOrderAmount ?? undefined,
      maxOrderAmount: maxOrderAmount ?? undefined,
      minOrderCount: minOrderCount ?? undefined,
      maxOrderCount: maxOrderCount ?? undefined,
      minProductCount: minProductCount ?? undefined,
      maxProductCount: maxProductCount ?? undefined,
      sortBy: sortBy || undefined,
      sortOrder: sortBy ? sortOrder || undefined : undefined,
    }),
    [
      userType,
      search,
      createdRange,
      minOrderAmount,
      maxOrderAmount,
      minOrderCount,
      maxOrderCount,
      minProductCount,
      maxProductCount,
      sortBy,
      sortOrder,
    ],
  );

  return {
    inputValue,
    setInputValue,
    search,
    createdRange,
    setCreatedRange,
    minOrderAmountInput,
    setMinOrderAmountInput,
    maxOrderAmountInput,
    setMaxOrderAmountInput,
    minOrderCountInput,
    setMinOrderCountInput,
    maxOrderCountInput,
    setMaxOrderCountInput,
    minProductCountInput,
    setMinProductCountInput,
    maxProductCountInput,
    setMaxProductCountInput,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    page,
    setPage,
    limit,
    setLimit,
    filterResetKey,
    bumpFilterResetKey,
    handleClearFilters,
    activeFilterCount,
    hasActiveFilters,
    listParams,
    exportParams,
  };
};
